'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type MouseEventHandler,
  type RefObject,
} from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { pauseHomepageLenis, resumeHomepageLenis } from 'utils/homepageLenis';
import CustomLink from 'components/shared/CustomLink';
import { useModal } from 'components/shared/Modal';
import { UhAppModal } from 'components/shared/UHAppDownloadModal';
import { BackgroundVideo } from 'components/landing/BackgroundVideo';
import { homeAssetSrc } from './homeAssetSrc';
import { bindOnceTextReveal, TEXT_REVEAL_FULL } from './scrollBeat';
import { homepageCtaProps } from './homepageCta';
import { AppStoreHandoffQr, PlayStoreHandoffQr } from './EmeraldStoreQr';
import {
  APPSTORE_URL,
  PLAYSTORE_URL,
  appHandoffUrl,
  tryOpenUltrahumanApp,
} from './openUltrahumanApp';
import v3 from './v3.module.css';
import styles from './EmeraldArcSection.module.css';

const DESKTOP_LAYOUT_QUERY = '(min-width: 992px)';

const EMERALD_VIDEO_SRC_DESKTOP = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/emerald/emerald-video.mp4'),
);
const EMERALD_VIDEO_SRC_MOBILE = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/emerald/emerald-video-ver.mp4'),
);
/* First frame of each emerald cut, so the poster the card shows while the video
   loads is the same picture the video opens on. Paired with the src below by the
   same `isDesktopLayout` gate, so the two cannot select different cuts. */
const EMERALD_POSTER_DESKTOP = getCompressedAssetUrl(
  homeAssetSrc('/web_v2/home-v4/video-posters/emerald-video/first-frame.jpg'),
);
const EMERALD_POSTER_MOBILE = getCompressedAssetUrl(
  homeAssetSrc(
    '/web_v2/home-v4/video-posters/emerald-video-ver/first-frame.jpg',
  ),
);

const ICON_APPSTORE = homeAssetSrc('/home/emerald/icon-appstore.svg');
const ICON_PLAYSTORE = homeAssetSrc('/home/emerald/icon-playstore.svg');
const ICON_PLAY = homeAssetSrc('/home/emerald/icon-play.svg');

const DynamicVideoJS = dynamic(() => import('components/heroes/VideoJS'), {
  ssr: false,
});

const VideoModalPlayer = ({
  videoSrc,
  portrait,
  onUnmount,
}: {
  videoSrc: string;
  portrait: boolean;
  onUnmount: () => void;
}) => {
  useEffect(() => onUnmount, [onUnmount]);

  const videoOptions = useMemo(
    () => ({
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      sources: [{ src: videoSrc, type: 'video/mp4' }],
      playsinline: true,
      preload: 'auto' as const,
      muted: false,
      loop: false,
      aspectRatio: portrait ? '9:16' : '16:9',
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    }),
    [videoSrc, portrait],
  );

  return (
    <div
      className={`${styles.modalContent} ${
        portrait ? styles.modalPortrait : ''
      }`}
    >
      <DynamicVideoJS key={videoSrc} options={videoOptions} />
    </div>
  );
};

/**
 * main wrapped ModalContext.hide and hand-managed html/body overflow plus
 * scroll restoration. This branch's Modal already unmounts its content on
 * hide and owns the overflow lock, so the film only has to pause Lenis and
 * the inline banner video while the modal is up.
 */
function useEmeraldVideoModal(
  bannerVideoRef: RefObject<HTMLVideoElement | null>,
) {
  const modal = useModal();

  const onClose = useCallback(() => {
    resumeHomepageLenis();
    modal.setBg('');
    const video = bannerVideoRef.current;
    if (!video || !video.paused) return;
    const rect = video.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      void video.play().catch(() => undefined);
    }
  }, [bannerVideoRef, modal]);

  return useCallback(() => {
    bannerVideoRef.current?.pause();
    pauseHomepageLenis();

    const isDesktop = window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
    modal.setBg('dark');
    modal.setCloseButtonTheme('light');
    modal.set(
      <VideoModalPlayer
        videoSrc={
          isDesktop ? EMERALD_VIDEO_SRC_DESKTOP : EMERALD_VIDEO_SRC_MOBILE
        }
        portrait={!isDesktop}
        onUnmount={onClose}
      />,
    );
    modal.setPortal(true);
    modal.show();
  }, [bannerVideoRef, modal, onClose]);
}

export const EmeraldArcSection = ({
  'aria-label': ariaLabel,
}: {
  'aria-label'?: string;
}) => {
  const t = useTranslations('homepage');

  const outerRef = useRef<HTMLElement | null>(null);
  const introRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_LAYOUT_QUERY);
    const update = () => setIsDesktopLayout(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  const emeraldVideoSrc = isDesktopLayout
    ? EMERALD_VIDEO_SRC_DESKTOP
    : EMERALD_VIDEO_SRC_MOBILE;
  const emeraldPoster = isDesktopLayout
    ? EMERALD_POSTER_DESKTOP
    : EMERALD_POSTER_MOBILE;

  const gsapCtx = useGsapContext(outerRef);
  const openVideoModal = useEmeraldVideoModal(videoRef);
  const modal = useModal();

  const openStoreModal = useCallback(
    (
      store: 'appstore' | 'playstore',
      trackClick?: MouseEventHandler<HTMLAnchorElement>,
    ) =>
      (event: MouseEvent<HTMLAnchorElement>) => {
        trackClick?.(event);
        const storeUrl = store === 'appstore' ? APPSTORE_URL : PLAYSTORE_URL;
        const handoffUrl = appHandoffUrl(
          store === 'appstore' ? 'ios' : 'android',
        );
        if (tryOpenUltrahumanApp(storeUrl)) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        modal.setBg('');
        modal.setPortal(false);
        modal.set(
          <UhAppModal
            heading={t('homepage.emerald.appModal.heading')}
            text={
              store === 'appstore'
                ? t('homepage.emerald.appModal.appstore.text')
                : t('homepage.emerald.appModal.playstore.text')
            }
            link={handoffUrl}
            qr={
              store === 'appstore' ? (
                <AppStoreHandoffQr
                  aria-label={t('homepage.emerald.appModal.appstore.qrAlt')}
                />
              ) : (
                <PlayStoreHandoffQr
                  aria-label={t('homepage.emerald.appModal.playstore.qrAlt')}
                />
              )
            }
          />,
        );
        modal.show();
      },
    [modal, t],
  );

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const intro = introRef.current;
      if (!intro) return undefined;
      const copies = Array.from(
        intro.querySelectorAll<HTMLElement>('[data-emerald-copy]'),
      );
      if (!copies.length) return undefined;
      return bindOnceTextReveal(copies, {
        ...TEXT_REVEAL_FULL,
        stagger: 0.06,
        trigger: intro,
      });
    });
  }, [gsapCtx]);

  const titleId = 'emerald-arc-title';
  const appStoreCta = homepageCtaProps(
    'emerald_appstore_button',
    'app_download_appstore_button',
  );
  const playStoreCta = homepageCtaProps(
    'emerald_playstore_button',
    'app_download_playstore_button',
  );

  return (
    <section
      ref={outerRef}
      className={styles.outer}
      aria-labelledby={titleId}
      aria-label={ariaLabel ?? t('homepage.emerald.aria.updateSection')}
    >
      <div className={styles.stage}>
        <div ref={introRef} className={styles.intro}>
          <div className={styles.introStack}>
            <h2 id={titleId} className={styles.title} data-emerald-copy>
              {t('homepage.emerald.updateTitle')}
            </h2>
            <p className={styles.body} data-emerald-copy>
              {t('homepage.emerald.updateBody')}
            </p>
          </div>
          <div className={styles.storeRow}>
            <CustomLink
              href={APPSTORE_URL}
              className={`${v3.pill} ${v3.pillSecondary} ${styles.storePill}`}
              {...appStoreCta}
              aria-label={t('homepage.emerald.aria.appstore')}
              onClick={openStoreModal('appstore', appStoreCta.onClick)}
            >
              {t('homepage.emerald.appstore')}
              <img
                src={ICON_APPSTORE}
                alt=""
                width={16}
                height={16}
                aria-hidden
              />
            </CustomLink>
            <CustomLink
              href={PLAYSTORE_URL}
              className={`${v3.pill} ${v3.pillSecondary} ${styles.storePill}`}
              {...playStoreCta}
              aria-label={t('homepage.emerald.aria.playstore')}
              onClick={openStoreModal('playstore', playStoreCta.onClick)}
            >
              {t('homepage.emerald.playstore')}
              <img
                src={ICON_PLAYSTORE}
                alt=""
                width={14}
                height={14}
                aria-hidden
              />
            </CustomLink>
          </div>
        </div>

        <div className={styles.filmLayer}>
          <div
            className={styles.filmCard}
            aria-label={t('homepage.emerald.aria.filmSection')}
          >
            <div className={styles.filmVideoFill}>
              <BackgroundVideo
                key={emeraldVideoSrc}
                ref={videoRef}
                src={emeraldVideoSrc}
                poster={emeraldPoster}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <button
              type="button"
              className={styles.watchPill}
              onClick={openVideoModal}
              data-buttontype="emerald_watch_button"
              aria-label={t('homepage.emerald.aria.watch')}
            >
              <img src={ICON_PLAY} alt="" width={24} height={24} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmeraldArcSection;
